/**
 * Filters a lead's treatment array down to only the treatments that belong to
 * the given custom tab. Used by the Consultation Attended table when a top-strip
 * custom tab is active so that each tab only shows the treatments it owns.
 *
 * Supports two scope modes:
 *   include — keep treatments whose PracticeTreatment id is in practice_treatments[].id
 *   exclude — keep treatments whose PracticeTreatment id is NOT in any excluded sibling tab
 *
 * Falls back to the full array when tab data is missing, preserving existing behaviour
 * for built-in / "All" tabs that carry no practice_treatments.
 *
 * @param {Array}  leadTreatments   item.lead_treatments from the V1 API response
 * @param {Object} activeCustomTab  the tab object from the Redux customTableTabs slice
 * @param {Array}  allTabsForParent all custom-tab objects for the same parent table
 * @returns {Array} filtered treatment array (may be empty if none match)
 */
export const filterLeadTreatmentsForCustomTab = (leadTreatments, activeCustomTab, allTabsForParent) => {
    if (!Array.isArray(leadTreatments) || !activeCustomTab) return leadTreatments || [];

    const scopeMode = activeCustomTab.treatment_scope_mode || 'include';
    const ownPts = activeCustomTab.practice_treatments || [];

    if (scopeMode === 'include') {
        if (!ownPts.length) return leadTreatments; // no treatment list → no filtering
        // practice_treatments items may be full objects {id, treatment_item:{id,...}} or bare IDs
        const includedPtIds = new Set(ownPts.map((pt) => pt?.id ?? pt));
        return leadTreatments.filter((lt) => {
            const ptId = lt?.treatment_data?.id ?? lt?.treatment_data;
            return ptId != null && includedPtIds.has(ptId);
        });
    }

    if (scopeMode === 'exclude') {
        const excludedSiblingIds = activeCustomTab.excluded_sibling_tab_ids || [];
        if (!excludedSiblingIds.length) return leadTreatments; // nothing excluded → keep all
        const tabs = Array.isArray(allTabsForParent) ? allTabsForParent : [];
        // Collect all PracticeTreatment IDs that belong to excluded sibling tabs
        const excludedPtIds = new Set();
        tabs.forEach((t) => {
            if (excludedSiblingIds.includes(t.id) || excludedSiblingIds.includes(String(t.id))) {
                (t.practice_treatments || []).forEach((pt) => {
                    const ptId = pt?.id ?? pt;
                    if (ptId != null) excludedPtIds.add(ptId);
                });
            }
        });
        if (!excludedPtIds.size) return leadTreatments; // excluded tabs have no treatments → keep all
        return leadTreatments.filter((lt) => {
            const ptId = lt?.treatment_data?.id ?? lt?.treatment_data;
            return ptId == null || !excludedPtIds.has(ptId);
        });
    }

    return leadTreatments; // unknown scope mode — safe fallback
};

export const handleLatestData = (item, tableType, rowStatus) => {
    let latestData = '';
    const consultantBookedType = tableType === 'ConsultantBooked';
    const allType = tableType === 'Dashboard' || tableType === 'NoTaskTable' || tableType === 'Whatsapp';
    const consultantAttendedType = tableType === 'ConsultantAttend' || tableType === 'AttendedChecklists';
    const consultantClinCheckAttendedType = tableType === 'ConsultantClinCheckAttend';
    const failedToAttendType = tableType === 'FailedToAttend';
    const treatmentStartedType = tableType === 'treatmentStarted' || tableType === 'treatmentStartedWithInvisalign';
    const closedType = tableType === 'Closed';
    const archType = tableType === 'ArchiveTable';
    const newType = tableType === 'NewLeadTable';
    const dashType = tableType === 'DashNewLeadTable';
    const getStatus = () => {
        if (allType || archType || rowStatus === 'All') return 'ALL';
        if (newType || dashType || rowStatus === 'NEW') return 'NEW';
        if (consultantAttendedType || consultantClinCheckAttendedType || rowStatus === 'CONSULTATION_ATTENDED') return 'CONSULTATION_ATTENDED';
        if (consultantBookedType || rowStatus === 'CONSULTATION_BOOKED') return 'CONSULTATION_BOOKED';
        if (failedToAttendType || rowStatus === 'FAILED_TO_ATTEND_CONSULTATION') return 'FAILED_TO_ATTEND_CONSULTATION';
        if (closedType || rowStatus === 'CLOSED') return 'CLOSED';
        if (treatmentStartedType || rowStatus === 'TREATMENT_STARTED') {
            return tableType === 'treatmentStartedWithInvisalign' ? 'TREATMENT_STARTED_INVISALIGN' : 'TREATMENT_STARTED';
        }
        return '';
    };
    const status = getStatus();
    switch (status) {
        case 'ALL':
            latestData = item.lead_treatments.length > 1
                ? item.lead_treatments.slice().sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                : item.lead_treatments;
            break;
        case 'NEW':
            latestData = item.lead_treatments
                .filter((el) => el.status === 'NEW')
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            break;
        case 'CONSULTATION_ATTENDED':
            latestData = item.lead_treatments
                .filter((el) => el.status === 'CONSULTATION_ATTENDED')
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            break;
        case 'CONSULTATION_BOOKED':
            latestData = item.lead_treatments
                .filter((el) => el.status === 'CONSULTATION_BOOKED')
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            break;
        case 'FAILED_TO_ATTEND_CONSULTATION':
            latestData = item.lead_treatments
                .filter((el) => el.status === 'FAILED_TO_ATTEND_CONSULTATION')
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            break;
        case 'CLOSED':
            latestData = item.lead_treatments
                .filter((el) => el.status === 'CLOSED')
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            break;
        case 'TREATMENT_STARTED_INVISALIGN':
            latestData = item.lead_treatments
                .filter((el) => el.status === 'TREATMENT_STARTED' && el.treatment === 'Invisalign')
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            break;
        case 'TREATMENT_STARTED':
            latestData = item.lead_treatments
                .filter((el) => el.status === 'TREATMENT_STARTED')
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            break;
        default:
            latestData = '';
            break;
    }
    return latestData;
};